import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import api3001 from "../../services/api3001";
import api3004 from "../../services/api3004";

/* ---------- ESTILO ---------- */

const Painel = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: "Inter", "Segoe UI", sans-serif;
`;

const Titulo = styled.h2`
  font-size: 26px;
  font-weight: 600;
  margin: 10px 0 20px;
  color: #222;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const Btn = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: #ececec;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #ddd;
  }
`;

const Strong = styled.strong`
  font-size: 16px;
`;

const Search = styled.input`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  margin-left: 15px;
  width: 220px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #888;
  }
`;

const TabelaWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;

  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;

  max-width: 75vw;
  max-height: 65vh;

  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
`;

const Tabela = styled.div`
  display: table;
  width: 100%;
  border-collapse: collapse;
`;

const Linha = styled.div`
  display: table-row;

  &:nth-child(even) {
    background: #fafafa;
  }

  &:hover {
    background: #f3f3f3;
  }
`;

const Celula = styled.div`
  display: table-cell;
  border-bottom: 1px solid #eee;
  text-align: center;
  padding: 12px;
  font-size: 14px;
`;

const HeaderCell = styled(Celula)`
  font-weight: 600;
  background: #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 3;
`;

const FixedColumn = styled.div`
  display: table-cell;
  position: sticky;
  left: 0;
  background: #f1f1f1;
  font-weight: 600;
  min-width: 180px;
  border-right: 1px solid #ddd;
  z-index: 5;
  padding: 12px;
`;

const Margem = styled.span`
  color: ${props =>
    props.valor > 30 ? "#2e7d32" :
    props.valor > 15 ? "#f9a825" :
    "#c62828"};
  font-weight: 600;
`;

/* ---------- COMPONENTE ---------- */

const RelatorioOnibus = () => {

  const [mesAtual, setMesAtual] = useState(new Date());
  const [dados, setDados] = useState([]);
  const [busca, setBusca] = useState("");

  const carregar = async () => {

    const mes = mesAtual.toISOString().slice(0,7);
    const onibus = await api3004.get("/api/onibus");
    const token = localStorage.getItem('token');

    const lista = await Promise.all(

      onibus.data.map(async (o)=>{

        try{

          const rel = await api3001.get("/api/orcamentos/viagens-onibus-mes",{
            headers: { Authorization: `Bearer ${token}` },
            params:{
              id_onibus:o.id,
              data:mes
            }
          });

          return{
            ...o,
            viagens: rel.data.total_viagens || 0,
            faturamento: rel.data.total_faturado || 0,
            faturamento_liquido: rel.data.faturamento_liquido || 0
          }

        }catch{

          return{
            ...o,
            viagens:0,
            faturamento:0,
            faturamento_liquido:0
          }

        }

      })

    );

    setDados(lista);

  }

  useEffect(()=>{

    carregar();

  },[mesAtual])


  const prevMonth = () => {

    setMesAtual(
      new Date(mesAtual.getFullYear(), mesAtual.getMonth() - 1, 1)
    );

  };

  const nextMonth = () => {

    setMesAtual(
      new Date(mesAtual.getFullYear(), mesAtual.getMonth() + 1, 1)
    );

  };

  const calcularMargem = (bruto, liquido) => {

    if(!bruto) return 0;

    return (liquido / bruto) * 100;

  }

  /* ---------- FILTRO INTELIGENTE ---------- */

  const dadosFiltrados = useMemo(()=>{

    if(!busca) return dados;

    const termo = busca.toLowerCase();

    return [...dados].sort((a,b)=>{

      const nomeA = (a.nome + a.placa).toLowerCase();
      const nomeB = (b.nome + b.placa).toLowerCase();

      const scoreA =
        nomeA.includes(termo) ? 0 : Math.abs(nomeA.length - termo.length);

      const scoreB =
        nomeB.includes(termo) ? 0 : Math.abs(nomeB.length - termo.length);

      return scoreA - scoreB;

    });

  },[busca,dados])

  return (

    <Painel>

      <Titulo>Relatório de Ônibus</Titulo>

      <Controls>

        <Btn onClick={prevMonth}>◀</Btn>

        <Strong>
          {mesAtual.toLocaleString("pt-BR",{
            month:"long",
            year:"numeric"
          })}
        </Strong>

        <Btn onClick={nextMonth}>▶</Btn>

        <Search
          placeholder="Buscar ônibus..."
          value={busca}
          onChange={e=>setBusca(e.target.value)}
        />

      </Controls>

      <TabelaWrapper>

        <Tabela>

          <Linha>
            <HeaderCell>Ônibus</HeaderCell>
            <HeaderCell>Total Viagens</HeaderCell>
            <HeaderCell>Faturamento Bruto</HeaderCell>
            <HeaderCell>Faturamento Líquido</HeaderCell>
            <HeaderCell>Margem</HeaderCell>
          </Linha>

          {dadosFiltrados.map((o)=>{

            const margem = calcularMargem(o.faturamento, o.faturamento_liquido) || 0;

            return(

              <Linha key={o.id}>

                <FixedColumn>
                  {o.prefixo} / {o.placa}
                </FixedColumn>

                <Celula>{o.viagens}</Celula>

                <Celula>
                  R$ {Number(o.faturamento / ((o.faturamento_dividido === 0) ? 1 : 2)).toLocaleString("pt-BR")}
                </Celula>

                <Celula>
                  R$ {Number(o.faturamento_liquido / ((o.faturamento_dividido === 0) ? 1 : 2)).toLocaleString("pt-BR")}
                </Celula>

                <Celula>
                  <Margem valor={margem}>
                    {margem.toFixed(1)}%
                  </Margem>
                </Celula>

              </Linha>

            )

          })}

        </Tabela>

      </TabelaWrapper>

    </Painel>

  );

}

export default RelatorioOnibus;